// Trip dialogs shared by the list, the trips and store mode: new trip, split by store,
// and the "another day / another store / cancel" options of an open trip.
import React, { useCallback, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText,
  MenuItem, Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import StoreIcon from '@mui/icons-material/Store';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { addDays, dayLabel, today, tripTakesText } from '../labels';
import { useConfirm, useNotify, useRun } from '../common';

export const tripTitle = (trip: T.TripSummary) => `${trip.storeName} · ${dayLabel(trip.plannedDate)}`;
export const isOpenTrip = (trip: T.TripSummary) => trip.status === 'Planned' || trip.status === 'InProgress';

/** "Hoje", "Amanhã", then "Quinta-feira, 23 out". */
const quickDayLabel = (day: T.DateOnly) => (day === today() ? 'Hoje' : day === addDays(today(), 1) ? 'Amanhã' : dayLabel(day));

/** "Que dia?": the next 8 days with one click each, plus any date. */
function DayDialog({ open, title, current, onClose }: {
  open: boolean; title: string; current?: T.DateOnly; onClose: (day: T.DateOnly | null) => void;
}) {
  const [other, setOther] = useState('');
  const days = Array.from({ length: 8 }, (_, i) => addDays(today(), i));
  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ px: 1 }}>
        <List dense>
          {days.map(day => (
            <ListItemButton key={day} selected={day === current} onClick={() => onClose(day)}>
              <ListItemText primary={quickDayLabel(day)} />
            </ListItemButton>
          ))}
        </List>
        <Stack direction="row" spacing={1} sx={{ px: 2, mt: 1 }} alignItems="center">
          <TextField
            type="date" size="small" label="Outro dia" value={other} onChange={e => setOther(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today() } }} sx={{ flexGrow: 1 }}
          />
          <Button disabled={!other} onClick={() => onClose(other)}>Escolher</Button>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={() => onClose(null)}>Cancelar</Button></DialogActions>
    </Dialog>
  );
}

/** A list of stores to pick one from. */
function StoreDialog({ open, title, stores, current, onClose }: {
  open: boolean; title: string; stores: T.Store[]; current?: T.Guid; onClose: (store: T.Store | null) => void;
}) {
  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <List sx={{ pt: 0 }}>
        {stores.map(s => (
          <ListItemButton key={s.publicId} selected={s.publicId === current} onClick={() => onClose(s)}>
            <ListItemIcon><StoreIcon /></ListItemIcon>
            <ListItemText primary={s.name} secondary={s.isDefault ? 'loja habitual' : undefined} />
          </ListItemButton>
        ))}
      </List>
      <DialogActions><Button onClick={() => onClose(null)}>Cancelar</Button></DialogActions>
    </Dialog>
  );
}

/**
 * New trip (store and day) and "Dividir por lojas". Both first offer to review the regular purchases that are due.
 * Render `element`; `afterChange` runs after a split.
 */
export function useTripPlanning(afterChange?: () => void) {
  const navigate = useNavigate();
  const run = useRun();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [newTrip, setNewTrip] = useState<{ stores: T.Store[]; needs: T.ShoppingNeed[]; storeId: T.Guid; date: T.DateOnly } | null>(null);
  const [saving, setSaving] = useState(false);
  const [splitResult, setSplitResult] = useState<string[] | null>(null);

  /** True when the user went to review them (the trip is left for later). */
  const reviewRecurringFirst = useCallback(async () => {
    const items = await run(() => api.getRecurringItems());
    const due = (items ?? []).filter(r => r.isDue && !r.isOnList).length;
    if (due === 0) return false;
    const text = due === 1 ? 'Há 1 compra regular por rever.' : `Há ${due} compras regulares por rever.`;
    if (!await confirm({ title: 'Compras regulares', text: `${text} Rever antes de criar a ida?`, confirmText: 'Rever', cancelText: 'Agora não' })) return false;
    navigate('/casa/compras/regulares');
    return true;
  }, [run, confirm, navigate]);

  const startNewTrip = useCallback(async () => {
    if (await reviewRecurringFirst()) return;
    const loaded = await run(() => Promise.all([api.getStores(), api.getShoppingNeeds()]));
    if (!loaded) return;
    const [stores, allNeeds] = loaded;
    if (stores.length === 0) {
      if (await confirm({ title: 'Lojas', text: 'Ainda não há lojas. Junta a primeira?', confirmText: 'Juntar' }))
        navigate('/casa/compras/lojas/nova');
      return;
    }
    const preferred = stores.find(s => s.isDefault) ?? stores[0];
    setNewTrip({ stores, needs: allNeeds.filter(n => n.status === 'Open'), storeId: preferred.publicId, date: today() });
  }, [reviewRecurringFirst, run, confirm, navigate]);

  const createTrip = async () => {
    if (!newTrip) return;
    setSaving(true);
    const trip = await run(() => api.createTrip({ storePublicId: newTrip.storeId, plannedDate: newTrip.date, needPublicIds: null }));
    setSaving(false);
    if (!trip) return;
    setNewTrip(null);
    navigate(`/casa/idas/${trip.publicId}`);
  };

  const split = useCallback(async () => {
    if (await reviewRecurringFirst()) return;
    if (!await confirm({
      title: 'Dividir por lojas',
      text: 'Cria uma ida por loja com o que está por comprar (na loja escolhida para cada artigo, ou onde foi comprado da última vez).',
      confirmText: 'Dividir',
    })) return;
    const result = await run(() => api.splitTrips(today()));
    if (!result) return;
    afterChange?.();
    const lines = result.trips.map(t => `• ${t.storeName}: ${t.itemCount} artigos`);
    if (result.addedToOpenTrips.length > 0) lines.push(`Juntado às idas já abertas em ${result.addedToOpenTrips.join(', ')}.`);
    if (result.withoutStore.length > 0) lines.push(`Sem loja (ficam em "Por comprar"): ${result.withoutStore.join(', ')}.`);
    if (lines.length === 0) notify('Não havia nada para dividir.', { severity: 'info' });
    else setSplitResult(lines);
  }, [reviewRecurringFirst, confirm, run, afterChange, notify]);

  const element = (
    <>
      {confirmDialog}
      <Dialog open={newTrip !== null} onClose={() => setNewTrip(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nova ida</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select label="Loja" value={newTrip?.storeId ?? ''}
              onChange={e => newTrip && setNewTrip({ ...newTrip, storeId: e.target.value })}
            >
              {(newTrip?.stores ?? []).map(s => <MenuItem key={s.publicId} value={s.publicId}>{s.name}</MenuItem>)}
            </TextField>
            {newTrip && (
              <Typography variant="body2" color="text.secondary">{tripTakesText(newTrip.needs, newTrip.storeId)}</Typography>
            )}
            <TextField
              type="date" label="Dia" value={newTrip?.date ?? ''}
              onChange={e => newTrip && e.target.value && setNewTrip({ ...newTrip, date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={newTrip ? quickDayLabel(newTrip.date) : undefined}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTrip(null)}>Cancelar</Button>
          <Button variant="contained" onClick={createTrip} disabled={saving}>Criar ida</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={splitResult !== null} onClose={() => setSplitResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Idas criadas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{splitResult?.join('\n')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitResult(null)}>OK</Button>
          <Button variant="contained" onClick={() => { setSplitResult(null); navigate('/casa/idas'); }}>Ver idas</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { startNewTrip, split, element };
}

/**
 * The options of an open trip: another day, another store (while the cart is empty) or cancel.
 * `onChanged` gets the updated trip, or null when it was cancelled.
 */
export function useTripOptions(onChanged: (trip: T.Trip | null) => void) {
  const run = useRun();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const [menu, setMenu] = useState<{ trip: T.TripSummary; cartEmpty: boolean } | null>(null);
  const [dayFor, setDayFor] = useState<T.TripSummary | null>(null);
  const [storeFor, setStoreFor] = useState<{ trip: T.TripSummary; stores: T.Store[] } | null>(null);

  const open = useCallback((trip: T.TripSummary, cartEmpty: boolean) => setMenu({ trip, cartEmpty }), []);

  const choose = async (choice: 'day' | 'store' | 'cancel') => {
    if (!menu) return;
    const { trip } = menu;
    setMenu(null);
    if (choice === 'day') setDayFor(trip);
    else if (choice === 'store') {
      const stores = await run(() => api.getStores());
      if (stores) setStoreFor({ trip, stores });
    } else {
      if (!await confirm({ title: 'Cancelar ida?', text: 'Tudo volta para "Por comprar".', confirmText: 'Cancelar ida', cancelText: 'Voltar', danger: true })) return;
      if (await run(() => api.cancelTrip(trip.publicId))) {
        notify('Ida cancelada.', { severity: 'success' });
        onChanged(null);
      }
    }
  };

  const changeDay = async (day: T.DateOnly | null) => {
    const trip = dayFor;
    setDayFor(null);
    if (!trip || !day || day === trip.plannedDate) return;
    const updated = await run(() => api.updateTrip(trip.publicId, { plannedDate: day, storePublicId: trip.storePublicId }));
    if (updated) onChanged(updated);
  };

  const changeStore = async (store: T.Store | null) => {
    const trip = storeFor?.trip;
    setStoreFor(null);
    if (!trip || !store || store.publicId === trip.storePublicId) return;
    const updated = await run(() => api.updateTrip(trip.publicId, { plannedDate: trip.plannedDate, storePublicId: store.publicId }));
    if (updated) onChanged(updated);
  };

  const element = (
    <>
      {confirmDialog}
      <Dialog open={menu !== null} onClose={() => setMenu(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{menu ? tripTitle(menu.trip) : ''}</DialogTitle>
        <List sx={{ pt: 0 }}>
          <ListItemButton onClick={() => choose('day')}>
            <ListItemIcon><EventIcon /></ListItemIcon>
            <ListItemText primary="Mudar o dia" secondary={menu ? dayLabel(menu.trip.plannedDate) : ''} />
          </ListItemButton>
          {menu?.cartEmpty && (
            <ListItemButton onClick={() => choose('store')}>
              <ListItemIcon><StoreIcon /></ListItemIcon>
              <ListItemText primary="Mudar de loja" secondary={menu.trip.storeName} />
            </ListItemButton>
          )}
          <ListItemButton onClick={() => choose('cancel')}>
            <ListItemIcon><CloseIcon color="error" /></ListItemIcon>
            <ListItemText primary="Cancelar ida" secondary='Tudo volta para "Por comprar"' slotProps={{ primary: { color: 'error' } }} />
          </ListItemButton>
        </List>
        {menu && !menu.cartEmpty && (
          <Alert severity="info" sx={{ mx: 2, mb: 1 }}>Com coisas no carrinho já não dá para mudar de loja.</Alert>
        )}
        <DialogActions><Button onClick={() => setMenu(null)}>Fechar</Button></DialogActions>
      </Dialog>
      <DayDialog open={dayFor !== null} title="Que dia?" current={dayFor?.plannedDate} onClose={changeDay} />
      <StoreDialog
        open={storeFor !== null} title="Que loja?" stores={storeFor?.stores ?? []}
        current={storeFor?.trip.storePublicId} onClose={changeStore}
      />
    </>
  );

  return { open, element };
}
